/**
 * Customer-facing safety story for /acceptance/proof (no backend contract changes).
 */

import type { ProofReadinessEvidence, ProofReadinessIssue, ProofReadinessSummary } from '../../services/reportsApi';
import { humanizeAreaLabel, humanizeZoneLabel } from './proofDisplayUtils';

export type SeverityMix = { high: number; medium: number; low: number; other: number };

function issueIncrement(issue?: ProofReadinessIssue): number {
  const c = issue?.count;
  return typeof c === 'number' && Number.isFinite(c) && c > 0 ? Math.round(c) : 1;
}

export function aggregateSeverityMix(issues: ProofReadinessIssue[]): SeverityMix {
  const mix: SeverityMix = { high: 0, medium: 0, low: 0, other: 0 };
  for (const issue of issues) {
    const sev = String(issue?.severity || '').toLowerCase();
    const n = issueIncrement(issue);
    if (sev === 'critical' || sev === 'high') mix.high += n;
    else if (sev === 'medium') mix.medium += n;
    else if (sev === 'low') mix.low += n;
    else mix.other += n;
  }
  return mix;
}

export function formatSeverityMix(mix: SeverityMix): string {
  const parts: string[] = [];
  if (mix.high) parts.push(`${mix.high} high/critical`);
  if (mix.medium) parts.push(`${mix.medium} medium`);
  if (mix.low) parts.push(`${mix.low} low`);
  if (mix.other) parts.push(`${mix.other} other`);
  return parts.length ? parts.join(' · ') : 'Not broken out';
}

function isZoneViolationIssue(issue?: ProofReadinessIssue): boolean {
  const blob = [issue?.id, issue?.title, issue?.kind].map((v) => String(v || '').toLowerCase()).join(' ');
  return blob.includes('zone_violation') || blob.includes('zone violation');
}

/** Subject–verb agreement for “X is/are concentrated in zone …”. */
export function dominantConcentrationClause(dominant: { label: string; count: number }, hotspotHuman: string): string {
  const subject = normalizeDominantSubject(dominant.label);
  const areaLabel = humanizeAreaLabel(hotspotHuman);
  if (/danger-zone entries/i.test(subject)) {
    // Epic 263 M4: do not lead with unique-"people" counts for this object.
    return `Repeated qualifying entries occurred in the danger zone in the ${areaLabel}.`;
  }
  const pluralish = looksPluralSubject(subject);
  const verb = pluralish ? 'are' : 'is';
  return `${subject} ${verb} concentrated in ${hotspotHuman}, a high-risk area.`;
}

export function dominantStrongestPatternClause(dominant: { label: string; count: number }): string {
  const subject = normalizeDominantSubject(dominant.label);
  const pluralish = looksPluralSubject(subject);
  const verb = pluralish ? 'are' : 'is';
  return `${subject} ${verb} the strongest recurring risk in this review window.`;
}

function normalizeDominantSubject(raw: string): string {
  const t = String(raw || '').trim().replace(/_/g, ' ');
  const lower = t.toLowerCase();
  if (lower === 'zone violation' || lower === 'zone violations') return 'Danger-zone entries';
  return t;
}

function looksPluralSubject(subject: string): boolean {
  const s = subject.toLowerCase();
  if (/\bviolations\b/.test(s)) return true;
  if (/\bentries\b/.test(s)) return true;
  if (/\bincidents\b/.test(s)) return true;
  if (/\bissues\b/.test(s)) return true;
  if (/\b(near misses|events|alerts)\b/.test(s)) return true;
  if (/[^s]s$/.test(s)) return true;
  return false;
}

function canonicalRiskPattern(summary?: ProofReadinessSummary | null) {
  return summary?.risk_pattern_summary || null;
}

function proofHotspotRaw(summary?: ProofReadinessSummary | null): string | null {
  return String(canonicalRiskPattern(summary)?.top_hotspot || summary?.top_hotspot || '').trim() || null;
}

export function dominantPatternLabel(
  issues: ProofReadinessIssue[],
  summary?: ProofReadinessSummary | null,
): { label: string; count: number } | null {
  const canonical = canonicalRiskPattern(summary);
  const canonicalLabel = String(canonical?.strongest_pattern_label || '').trim();
  const canonicalCount = Number(canonical?.strongest_pattern_count || 0);
  if (canonicalLabel && canonicalCount > 0) {
    return { label: canonicalLabel, count: canonicalCount };
  }
  const ranked = [...issues]
    .filter((issue) => {
      const kind = String(issue?.kind || '').toLowerCase();
      const id = String(issue?.id || '').toLowerCase();
      if (kind === 'hotspot') return false;
      if (isProofGapIssue(issue)) return false;
      if (id === 'group:zone_violation') return true;
      return true;
    })
    .map((issue) => {
      const id = String(issue?.id || '').toLowerCase();
      const rawTitle = String(issue?.title || 'Safety event').trim();
      const label = id === 'group:zone_violation' || isZoneViolationIssue(issue)
        ? 'Danger-zone entries'
        : rawTitle.replace(/^Repeated\s+/i, '').replace(/_/g, ' ').trim();
      return {
        label,
        count: issueIncrement(issue),
      };
    })
    .filter((issue) => issue.label)
    .sort((a, b) => b.count - a.count);
  const top = ranked[0];
  if (!top || top.count <= 0) return null;
  return top;
}

export function hotspotIssueCount(issues: ProofReadinessIssue[], topHotspotRaw?: string | null): number | null {
  const zoneKey = String(topHotspotRaw || '').trim().toLowerCase();
  if (!zoneKey) return null;
  for (const issue of issues) {
    const kind = String(issue?.kind || '').toLowerCase();
    if (kind !== 'hotspot') continue;
    const title = String(issue?.title || '').toLowerCase();
    const id = String(issue?.id || '').toLowerCase();
    if (title.includes(zoneKey) || id.includes(zoneKey.replace(/\s+/g, '_'))) {
      return typeof issue.count === 'number' && Number.isFinite(issue.count) ? Math.round(issue.count) : null;
    }
  }
  const human = humanizeZoneLabel(topHotspotRaw).toLowerCase();
  for (const issue of issues) {
    const kind = String(issue?.kind || '').toLowerCase();
    if (kind !== 'hotspot') continue;
    const title = String(issue?.title || '').toLowerCase();
    if (human && title.includes(human)) {
      return typeof issue.count === 'number' && Number.isFinite(issue.count) ? Math.round(issue.count) : null;
    }
  }
  return null;
}

export function repeatPatternIndicator(
  issues: ProofReadinessIssue[],
  summary?: ProofReadinessSummary | null,
): boolean {
  const canonical = canonicalRiskPattern(summary);
  if (typeof canonical?.repeat_pattern_count === 'number') {
    return canonical.repeat_pattern_count > 0;
  }
  return issues.some((issue) => {
    const kind = String(issue?.kind || '').toLowerCase();
    const title = String(issue?.title || '').toLowerCase();
    const c = issue?.count;
    if (kind.includes('repeated') || title.includes('repeated')) return true;
    if (typeof c === 'number' && c >= 3) return true;
    return false;
  });
}

export function repeatPatternCount(
  issues: ProofReadinessIssue[],
  summary?: ProofReadinessSummary | null,
): number {
  const canonical = canonicalRiskPattern(summary);
  if (typeof canonical?.repeat_pattern_count === 'number' && Number.isFinite(canonical.repeat_pattern_count)) {
    return canonical.repeat_pattern_count;
  }
  return issues.filter((issue) => {
    const kind = String(issue?.kind || '').toLowerCase();
    const title = String(issue?.title || '').toLowerCase();
    const c = issue?.count;
    return kind.includes('repeated') || title.includes('repeated') || (typeof c === 'number' && c >= 3);
  }).length;
}

export function buildSafetyConclusionText(params: {
  summary?: ProofReadinessSummary | null;
  issues: ProofReadinessIssue[];
  caseFileSummary?: { incidents_total?: number } | null;
}): string {
  const hotspotRaw = proofHotspotRaw(params.summary);
  const hotspot = hotspotRaw ? humanizeZoneLabel(hotspotRaw) : '';
  const dominant = dominantPatternLabel(params.issues, params.summary);
  const repeat = repeatPatternIndicator(params.issues, params.summary);
  const highRisk =
    typeof params.summary?.high_risk_incidents === 'number' && Number.isFinite(params.summary.high_risk_incidents)
      ? params.summary.high_risk_incidents
      : null;
  const insufficientEvidence =
    typeof params.summary?.insufficient_evidence_incidents === 'number' && Number.isFinite(params.summary.insufficient_evidence_incidents)
      ? params.summary.insufficient_evidence_incidents
      : 0;
  const demoOrTestActivity =
    typeof params.summary?.demo_or_test_activity_incidents === 'number' && Number.isFinite(params.summary.demo_or_test_activity_incidents)
      ? params.summary.demo_or_test_activity_incidents
      : 0;
  const total = totalIncidentsForWindow(params.summary, params.caseFileSummary);

  const parts: string[] = [];
  if (dominant && hotspot) {
    parts.push(dominantConcentrationClause(dominant, hotspot));
  } else if (dominant) {
    parts.push(dominantStrongestPatternClause(dominant));
  } else if (hotspot) {
    parts.push(`Risk is concentrated in the ${humanizeAreaLabel(hotspot)}.`);
  }

  if (highRisk === 0 && repeat) {
    parts.push('No high-risk incident was observed, but recurring signals suggest a recurring pedestrian-flow or zone-control issue.');
  } else if (highRisk === 0) {
    parts.push('No high-risk incident was observed in this window.');
  } else if (highRisk != null && highRisk > 0) {
    parts.push(`${highRisk} high-risk incident${highRisk === 1 ? '' : 's'} ${highRisk === 1 ? 'was' : 'were'} observed and should shape the first corrective action.`);
  }

  if (repeat && highRisk !== 0) {
    parts.push('Recurring signals suggest a recurring pedestrian-flow or zone-control issue.');
  } else if (total != null && total > 0) {
    parts.push('The current window is more isolated than repetitive, so confirm whether the same recurring risk persists in the next window.');
  }

  if (insufficientEvidence > 0) {
    parts.push(`${insufficientEvidence} reviewed incident${insufficientEvidence === 1 ? '' : 's'} remained inconclusive because evidence was insufficient.`);
  }
  if (demoOrTestActivity > 0) {
    parts.push(`${demoOrTestActivity} incident${demoOrTestActivity === 1 ? '' : 's'} in this window ${demoOrTestActivity === 1 ? 'was' : 'were'} marked as special-case activity measured outside normal operating conditions and should not be treated as a verified operational improvement.`);
  }

  return parts.join(' ').trim()
    || 'Edgentik found a safety risk worth reviewing in this window; use the concentration, evidence strength, and intervention sections below to decide what to reduce first.';
}

export type EvidenceConfidenceTier = 'complete' | 'partial' | 'weak';

export function evidenceConfidenceTier(params: {
  summary?: ProofReadinessSummary | null;
  evidence?: ProofReadinessEvidence | null;
  clipCaptureDisabled: boolean;
}): EvidenceConfidenceTier {
  if (params.clipCaptureDisabled) return 'weak';
  const st = String(params.summary?.evidence_operator_status || '').toLowerCase();
  if (st === 'failing') return 'weak';
  if (st === 'degraded') return 'partial';
  const rate = params.summary?.evidence_coverage_rate;
  if (typeof rate === 'number' && rate < 0.85) return 'partial';
  const missing = params.evidence?.missing_high_risk_clips;
  if (typeof missing === 'number' && missing > 0) return 'partial';
  if (st === 'healthy') return 'complete';
  if (typeof rate === 'number' && rate >= 0.92) return 'complete';
  return 'partial';
}

export function evidenceConfidenceLabel(tier: EvidenceConfidenceTier): string {
  if (tier === 'complete') return 'Complete';
  if (tier === 'partial') return 'Partial';
  return 'Weak';
}

export function riskPresentationVisuals(visuals?: any[] | null): any[] {
  if (!Array.isArray(visuals)) return [];
  return visuals.filter((v) => {
    const id = String(v?.id || '').toLowerCase();
    const t = String(v?.type || '').toLowerCase();
    if (t === 'severity_mix') return true;
    if (t === 'top_n_bar' || t === 'stacked_bar') {
      if (/hotspot|zone|area|repeat/i.test(id)) return true;
      const title = String(v?.title || '').toLowerCase();
      if (/hotspot|zone|area|repeat|severity/i.test(title)) return true;
    }
    return false;
  });
}

export function totalIncidentsForWindow(
  summary?: ProofReadinessSummary | null,
  caseFileSummary?: { incidents_total?: number } | null,
): number | null {
  if (typeof summary?.total_incidents === 'number' && Number.isFinite(summary.total_incidents)) {
    return summary.total_incidents;
  }
  const raw = caseFileSummary?.incidents_total;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  return null;
}

/** True when the window has enough pattern signal to prioritize floor safety (vs. only gaps/blockers). */
export function hasUsableSafetyPattern(params: {
  summary?: ProofReadinessSummary | null;
  issues?: ProofReadinessIssue[];
  caseFileSummary?: { incidents_total?: number } | null;
}): boolean {
  const issues = Array.isArray(params.issues) ? params.issues : [];
  const total = totalIncidentsForWindow(params.summary, params.caseFileSummary);
  if (total != null && total > 0) return true;
  if (dominantPatternLabel(issues, params.summary)) return true;
  if (proofHotspotRaw(params.summary)) return true;
  const hr = params.summary?.high_risk_incidents;
  if (typeof hr === 'number' && Number.isFinite(hr) && hr > 0) return true;
  return false;
}

/** Issues that mainly explain weak formal proof / capture / detection signals (not “what happened on the floor”). */
export function isProofGapIssue(issue?: ProofReadinessIssue): boolean {
  const id = String(issue?.id || '').toLowerCase();
  const kind = String(issue?.kind || '').toLowerCase();
  const title = String(issue?.title || '').toLowerCase();
  if (id.startsWith('evidence:')) return true;
  if (kind === 'evidence_gap') return true;
  if (id === 'ui:inference_pipeline_degraded') return true;
  if (title.includes('metrics unavailable') || title.includes('metric unavailable')) return true;
  return false;
}

export function hotspotConcentrated(
  issues: ProofReadinessIssue[],
  summary?: ProofReadinessSummary | null,
  caseFileSummary?: { incidents_total?: number } | null,
): boolean {
  const canonical = canonicalRiskPattern(summary);
  if (typeof canonical?.concentration_percent === 'number' && Number.isFinite(canonical.concentration_percent)) {
    return canonical.concentration_percent >= 25;
  }
  const total = totalIncidentsForWindow(summary, caseFileSummary);
  const raw = proofHotspotRaw(summary);
  if (!raw || total == null || total <= 0) return false;
  const hc = hotspotIssueCount(issues, raw);
  if (hc == null || hc <= 0) return false;
  return hc / total >= 0.25;
}

export function percentConcentration(
  issues: ProofReadinessIssue[],
  summary?: ProofReadinessSummary | null,
  caseFileSummary?: { incidents_total?: number } | null,
): number | null {
  const canonical = canonicalRiskPattern(summary);
  if (typeof canonical?.concentration_percent === 'number' && Number.isFinite(canonical.concentration_percent)) {
    return canonical.concentration_percent;
  }
  const total = totalIncidentsForWindow(summary, caseFileSummary);
  const hotspotRaw = proofHotspotRaw(summary);
  const hc = hotspotIssueCount(issues, hotspotRaw);
  if (total == null || total <= 0 || hc == null || hc <= 0) return null;
  return Math.round((hc / total) * 1000) / 10;
}

export function buildWhyItMattersText(params: {
  summary?: ProofReadinessSummary | null;
  issues: ProofReadinessIssue[];
  openIncidents: number | null;
  caseFileSummary?: { incidents_total?: number } | null;
}): string {
  const { summary, issues, openIncidents, caseFileSummary } = params;
  const repeat = repeatPatternIndicator(issues, summary);
  const concentrated = hotspotConcentrated(issues, summary, caseFileSummary);
  const total = totalIncidentsForWindow(summary, caseFileSummary);
  const open = openIncidents != null && openIncidents > 0 ? openIncidents : 0;
  const insufficientEvidence =
    typeof summary?.insufficient_evidence_incidents === 'number' && Number.isFinite(summary.insufficient_evidence_incidents)
      ? summary.insufficient_evidence_incidents
      : 0;
  const demoOrTestActivity =
    typeof summary?.demo_or_test_activity_incidents === 'number' && Number.isFinite(summary.demo_or_test_activity_incidents)
      ? summary.demo_or_test_activity_incidents
      : 0;

  if (repeat && concentrated) {
    return (
      'This looks like a recurring risk with concentration in one high-risk area, so it deserves a targeted corrective action '
      + 'rather than treating events as unrelated one-offs.'
    );
  }
  if (repeat) {
    return (
      'The same type of event shows up often enough that it likely reflects how work is organized or supervised—not random noise.'
    );
  }
  if (concentrated) {
    return (
      'A large share of activity sits in one high-risk area, so improvements there should reduce exposure the fastest.'
    );
  }
  if (insufficientEvidence > 0 && open <= 1) {
    return 'Some incidents were reviewed but remained inconclusive, so strengthen evidence capture before using this window as a firm baseline.';
  }
  if (demoOrTestActivity > 0 && total != null && demoOrTestActivity >= Math.max(1, Math.ceil(total / 2))) {
    return 'A meaningful share of this window was marked as special-case activity, so separate that activity from standard operational reporting before sharing conclusions externally.';
  }
  if (total != null && total > 0 && open <= 1) {
    return 'Volume is limited in this window; treat findings as directional unless the next review window confirms the same shape.';
  }
  if (open > 0) {
    return 'These are mostly individual open items across the window; prioritize clearing the highest-severity items first.';
  }
  return 'Use the risk and area signals below to decide where a walk-through or control change will reduce exposure first.';
}

export function buildRecommendedOperationalAction(params: {
  summary?: ProofReadinessSummary | null;
  issues: ProofReadinessIssue[];
  openIncidents: number | null;
}): string {
  const hotspotRaw = proofHotspotRaw(params.summary);
  const hotspot = hotspotRaw ? humanizeZoneLabel(hotspotRaw) : '';
  const zoneHeavy = params.issues.some(isZoneViolationIssue);
  const repeat = repeatPatternIndicator(params.issues, params.summary);
  const open = params.openIncidents != null && params.openIncidents > 0 ? params.openIncidents : 0;
  const insufficientEvidence =
    typeof params.summary?.insufficient_evidence_incidents === 'number' && Number.isFinite(params.summary.insufficient_evidence_incidents)
      ? params.summary.insufficient_evidence_incidents
      : 0;
  const demoOrTestActivity =
    typeof params.summary?.demo_or_test_activity_incidents === 'number' && Number.isFinite(params.summary.demo_or_test_activity_incidents)
      ? params.summary.demo_or_test_activity_incidents
      : 0;
  const total =
    typeof params.summary?.total_incidents === 'number' && Number.isFinite(params.summary.total_incidents)
      ? params.summary.total_incidents
      : null;

  if (hotspot && zoneHeavy) {
    const areaLabel = humanizeAreaLabel(hotspot);
    if (/pedestrian|crosswalk|walkway|footpath/i.test(hotspot)) {
      return `Review pedestrian controls in the ${areaLabel}.`;
    }
    return (
      `Run a focused walk-through of the ${areaLabel} with frontline leads: check signage, crossing paths, and whether controls match how people actually move.`
    );
  }
  if (hotspot && repeat) {
    return (
      `Schedule a short review in the ${humanizeAreaLabel(hotspot)} with operations and safety leads, and agree on one concrete change to trial before the next review window.`
    );
  }
  if (hotspot) {
    return `Spend the next review cycle observing the ${humanizeAreaLabel(hotspot)} during peak activity and note what repeatedly drives exposure.`;
  }
  if (insufficientEvidence > 0) {
    return 'Strengthen snapshot and clip capture for inconclusive incidents, then rerun this reporting period before treating it as a firm baseline.';
  }
  if (demoOrTestActivity > 0 && total != null && demoOrTestActivity >= Math.max(1, Math.ceil(total / 2))) {
    return 'Separate special-case activity from standard operational reporting before sharing this window with stakeholders.';
  }
  if (open > 5) {
    return 'Triage open items by severity and zone, then close the top batch with documented mitigations before widening scope.';
  }
  if (open > 0) {
    return 'Close remaining open items with clear owners and dates, starting with anything people could be exposed to again.';
  }
  return 'If this window stays quiet, keep sampling future shifts so you catch regressions early.';
}

function extractHourBucket(value: unknown): number | null {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const dt = new Date(raw);
  if (Number.isNaN(dt.getTime())) return null;
  return dt.getUTCHours();
}

export function timeOfDayPeakLabel(params: {
  timeline?: Array<any> | null;
  incidents?: Array<any> | null;
}): string | null {
  const counts = new Map<number, number>();
  const pushHour = (value: unknown) => {
    const hour = extractHourBucket(value);
    if (hour == null) return;
    counts.set(hour, (counts.get(hour) || 0) + 1);
  };

  for (const item of Array.isArray(params.timeline) ? params.timeline : []) {
    pushHour(item?.ts || item?.created_at || item?.time);
  }
  for (const item of Array.isArray(params.incidents) ? params.incidents : []) {
    pushHour(item?.created_at || item?.ts_start || item?.ts || item?.opened_at);
  }

  let bestHour: number | null = null;
  let bestCount = 0;
  counts.forEach((count, hour) => {
    if (count > bestCount) {
      bestHour = hour;
      bestCount = count;
    }
  });
  if (bestHour == null || bestCount <= 0) return null;
  const next = (bestHour + 1) % 24;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(bestHour)}:00-${pad(next)}:00 UTC`;
}
